import { UserFeatures } from '@/services/firebaseServices/AuthServices/types';

/**
 * Centralized Permission Constants
 *
 * This is the SINGLE SOURCE OF TRUTH for all route and navigation permissions.
 * These constants MUST be used in:
 * 1. Navigation configuration (src/constants/navigation.ts)
 * 2. Layout guards (src/app/dashboard/[route]/layout.tsx)
 * 3. Any page-level permission checks
 *
 * Permission Logic: User must have at least ONE of the listed features to access
 *
 * Feature Mappings (from backend):
 * - SuperAdmin: ABTestFeature, ContentEditing, CertificateReview, ContentReview, SpaceManagement
 * - Admin: ContentEditing, CertificateReview, ContentReview, SpaceManagement
 * - SpaceOwner: SpaceManagement, CertificateReview, ContentReview
 * - Editor: ContentEditing, CertificateReview
 * - ContentManager: ContentEditing
 * - ContentReviewer: ContentReview
 * - CertificateReviewer: CertificateReview
 */

/**
 * Certificates: SuperAdmin, Admin, SpaceOwner, Editor, CertificateReviewer
 * Feature: CertificateReview
 */
export const CERTIFICATES_PERMISSIONS: UserFeatures[] = [
  UserFeatures.CertificateReview,
];

/**
 * Profiles: SuperAdmin, Admin, SpaceOwner, Editor
 * Features: SpaceManagement OR CertificateReview
 * NOT: ContentManager, ContentReviewer, CertificateReviewer
 * Note: CertificateReviewer gets access as a side effect (has CertificateReview)
 */
export const PROFILES_PERMISSIONS: UserFeatures[] = [
  UserFeatures.SpaceManagement,
  UserFeatures.CertificateReview,
];

/**
 * Job Profiles: SuperAdmin, Admin, SpaceOwner, Editor, ContentManager
 * Features: ContentEditing OR SpaceManagement
 * NOT: ContentReviewer, CertificateReviewer
 *
 * Backend permissions: 'list:jobProfiles', 'get:jobProfile', 'create:jobProfile',
 * 'update:jobProfile', 'delete:jobProfile'
 * Roles allowed: super-admin, admin, space-owner, editor, content-manager
 */
export const JOB_PROFILE_PERMISSIONS: UserFeatures[] = [
  UserFeatures.ContentEditing,
  UserFeatures.SpaceManagement,
];

/**
 * Skill Matrix: SuperAdmin, Admin, SpaceOwner, Editor, CertificateReviewer
 * Feature: CertificateReview
 * NOT: ContentManager, ContentReviewer
 *
 * Backend permission: 'list:skillMatrix'
 * Roles allowed: super-admin, admin, space-owner, editor, certificate-reviewer
 * (same role set as Certificates)
 */
export const SKILL_MATRIX_PERMISSIONS: UserFeatures[] = [
  UserFeatures.CertificateReview,
];

/**
 * Courses: SuperAdmin, Admin, SpaceOwner, Editor, ContentManager, ContentReviewer
 * Features: ContentEditing OR ContentReview
 */
export const COURSES_PERMISSIONS: UserFeatures[] = [
  UserFeatures.ContentEditing,
  UserFeatures.ContentReview,
];

/**
 * Sequences: SuperAdmin, Admin, SpaceOwner, Editor, ContentManager, ContentReviewer
 * Features: ContentEditing OR ContentReview
 */
export const SEQUENCES_PERMISSIONS: UserFeatures[] = [
  UserFeatures.ContentEditing,
  UserFeatures.ContentReview,
];

/**
 * Quizzes: SuperAdmin, Admin, SpaceOwner, Editor, ContentManager, ContentReviewer
 * Features: ContentEditing OR ContentReview
 */
export const QUIZZES_PERMISSIONS: UserFeatures[] = [
  UserFeatures.ContentEditing,
  UserFeatures.ContentReview,
];

/**
 * Gallery: SuperAdmin, Admin, SpaceOwner, Editor, ContentManager
 * Features: ContentEditing OR SpaceManagement
 * NOT: ContentReviewer, CertificateReviewer
 */
export const GALLERY_PERMISSIONS: UserFeatures[] = [
  UserFeatures.ContentEditing,
  UserFeatures.SpaceManagement,
];

/**
 * Glossary: SuperAdmin, Admin, SpaceOwner, Editor, ContentManager
 * Features: ContentEditing OR SpaceManagement
 * NOT: ContentReviewer, CertificateReviewer
 * Note: "if AB test active" is a space setting, not a user permission
 */
export const GLOSSARY_PERMISSIONS: UserFeatures[] = [
  UserFeatures.ContentEditing,
  UserFeatures.SpaceManagement,
];

/**
 * AB Tests: SuperAdmin only
 * Feature: ABTestFeature
 */
export const ABTEST_PERMISSIONS: UserFeatures[] = [
  UserFeatures.ABTestFeature,
];

/**
 * Space Code / Trainer App Login: SuperAdmin, Admin, SpaceOwner, Editor, ContentManager
 * Features: ContentEditing OR SpaceManagement
 * NOT: ContentReviewer, CertificateReviewer
 *
 * Backend permission: 'get:spaceCode'
 * Roles allowed: super-admin, admin, space-owner, editor, content-manager
 */
export const SPACE_CODE_PERMISSIONS: UserFeatures[] = [
  UserFeatures.ContentEditing,
  UserFeatures.SpaceManagement,
];

/**
 * Settings: SuperAdmin, Admin, SpaceOwner
 * Feature: SpaceManagement
 * NOT: Editor, ContentManager, ContentReviewer, CertificateReviewer
 *
 * Settings page allows configuring space-level settings like languages,
 * access controls, and other administrative features.
 */
export const SETTINGS_PERMISSIONS: UserFeatures[] = [
  UserFeatures.SpaceManagement,
];

/**
 * Preferences: SpaceOwner, Editor, CertificateReviewer
 * Feature: CertificateReview
 * NOT: ContentManager, ContentReviewer
 *
 * Note: SuperAdmin and Admin have CertificateReview feature but per documentation
 * they should access Settings instead. The feature-based system gives them access
 * as a side effect, which may be acceptable for backward compatibility.
 *
 * Preferences page allows users to configure personal settings like
 * training report subscriptions.
 */
export const PREFERENCES_PERMISSIONS: UserFeatures[] = [
  UserFeatures.CertificateReview,
];
